import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../lib/AuthContext.js';
import { ApiClient } from '../lib/api.js';
import { WritePanel } from './Write.js';
import { EngagePanel } from './Radar.js';

type Stage = 'loading' | 'persona' | 'handle' | 'processing' | 'suggest' | 'extension' | 'dashboard';

interface SuggestedTopic { topic: string; why?: string }

const EXT_BANNER_DISMISS_KEY = 'tv_ext_banner_dismissed';
const APPROVAL_SEEN_KEY = 'tv_approval_welcome_seen';

export function Home() {
  const { user, getToken } = useAuth();
  const api = new ApiClient(getToken);

  const [stage, setStage] = useState<Stage>('loading');
  const [me, setMe] = useState<any>(null);
  const [monitors, setMonitors] = useState<any[]>([]);
  const [posts, setPosts] = useState<any[]>([]);
  const [topPost, setTopPost] = useState<any>(null);

  useEffect(() => {
    (async () => {
      try {
        const m = await api.me();
        setMe(m);
        const hasPersona = (m?.user?.personas?.length ?? 0) > 0 || (m?.user?.personaCustom ?? '').length > 0;
        const ob = m?.user?.onboarding ?? {};
        if (ob.voiceProfileReady && hasPersona) {
          const [mons, radar] = await Promise.all([
            api.monitors().catch(() => ({ monitors: [] })),
            api.radar().catch(() => ({ posts: [] })),
          ]);
          setMonitors(mons.monitors ?? []);
          setPosts(radar.posts ?? []);
          setTopPost(radar.posts?.[0] ?? null);
          setStage('dashboard');
        } else if (!hasPersona) {
          setStage('persona');
        } else if (ob.tweetsImported && !ob.voiceProfileReady) {
          // Tweets already imported but voice wasn't generated (user navigated away mid-onboarding).
          // Resume from voice generation instead of forcing them to re-scrape.
          setStage('processing');
        } else {
          setStage('handle');
        }
      } catch { setStage('persona'); }
    })();
    /* eslint-disable-next-line */
  }, []);

  if (stage === 'loading') return <div className="loading">Loading…</div>;
  if (stage === 'persona') return <PersonaStage api={api} user={user} onDone={() => setStage('handle')} />;
  if (stage === 'handle') return <HandleStage api={api} user={user} onDone={() => setStage('processing')} onSkip={() => setStage('suggest')} />;
  if (stage === 'processing') return <ProcessingStage api={api} onDone={() => setStage('suggest')} />;
  if (stage === 'suggest') return <SuggestStage api={api} me={me} onDone={() => setStage('extension')} onSkip={() => setStage('extension')} />;
  if (stage === 'extension') return <ExtensionStage onDone={async () => {
    // Refresh dashboard data on the way in.
    const [m, mons, radar] = await Promise.all([api.me(), api.monitors(), api.radar()]);
    setMe(m); setMonitors(mons.monitors ?? []); setPosts(radar.posts ?? []); setTopPost(radar.posts?.[0] ?? null);
    setStage('dashboard');
  }} />;
  return <Dashboard api={api} user={user} me={me} monitors={monitors} posts={posts} topPost={topPost} />;
}

// =========================================================================
// Stage 1 — Welcome + handle input
// =========================================================================
function HandleStage({ api, user, onDone, onSkip }: { api: ApiClient; user: any; onDone: () => void; onSkip: () => void }) {
  const [handle, setHandle] = useState('');
  const [showPaste, setShowPaste] = useState(false);
  const [pasted, setPasted] = useState('');
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState<string>('');
  const [err, setErr] = useState<string | null>(null);
  const [extStatus, setExtStatus] = useState<'unknown' | 'installed' | 'missing'>('unknown');

  // Check the extension once on mount; recheck whenever the user clicks Retry below.
  useEffect(() => {
    (async () => {
      const { pingExtension } = await import('../lib/extensionBridge.js');
      const r = await pingExtension();
      setExtStatus(r.installed ? 'installed' : 'missing');
    })();
  }, []);

  const name = (user?.displayName || user?.email?.split('@')[0] || 'there') as string;
  const clean = handle.trim().replace(/^@/, '');
  const validHandle = /^[A-Za-z0-9_]{1,15}$/.test(clean);

  async function recheckExtension() {
    setExtStatus('unknown');
    const { pingExtension } = await import('../lib/extensionBridge.js');
    const r = await pingExtension();
    setExtStatus(r.installed ? 'installed' : 'missing');
  }

  async function submitHandle() {
    if (!validHandle) return;
    setBusy(true); setErr(null); setProgress('');
    try {
      if (extStatus !== 'installed') {
        setErr('The tweetvia extension isn\'t connected yet. Install or sign in to it, then click Retry.');
        setBusy(false);
        return;
      }
      setProgress('Opening your X profile…');
      const { scrapeProfileViaExtension } = await import('../lib/extensionBridge.js');
      const scraped = await scrapeProfileViaExtension(clean);

      if (scraped.blocked) {
        setErr('X is asking you to sign in before we can read your profile. Open x.com in this browser, sign in, then click Try again below.');
        setBusy(false);
        return;
      }
      if (!scraped.tweets || scraped.tweets.length === 0) {
        setErr('We couldn\'t find any tweets on your profile. You can paste a few below, or sign in to x.com and try again.');
        setShowPaste(true);
        setBusy(false);
        return;
      }

      setProgress(`Saving ${scraped.tweets.length} tweets…`);
      await api.importScraped({
        handle: clean,
        tweets: scraped.tweets,
        displayName: scraped.displayName,
        bio: scraped.bio,
        profileImageUrl: scraped.profileImageUrl,
        bannerImageUrl: scraped.bannerImageUrl,
        pinnedTweetText: scraped.pinnedTweetText,
        followers: scraped.followers,
        following: scraped.following,
        verified: scraped.verified,
      });
      onDone();
    } catch (e: any) {
      setErr(`Couldn't reach the extension: ${e.message}. Try paste below.`);
      setShowPaste(true);
      setBusy(false);
    }
  }

  async function submitPaste() {
    const tweets = pasted.split(/\n\s*\n+|\n---+\n/).map(s => s.trim()).filter(Boolean);
    if (tweets.length < 3) { setErr(`Need at least 3 tweets. You pasted ${tweets.length}.`); return; }
    setBusy(true); setErr(null); setProgress('Saving your tweets…');
    try { await api.importPaste(tweets); onDone(); }
    catch (e: any) { setErr(e.message); setBusy(false); }
  }

  return (
    <div className="page">
      <div className="page-head">
        <h1>Welcome, {name}.</h1>
        <p className="lede">
          Your place to <strong>write better tweets</strong>, <strong>find conversations to engage</strong>, and <strong>grow your audience</strong>.
        </p>
      </div>

      <div className="card highlight onboarding-card">
        <h3 style={{ margin: '0 0 6px' }}>Let's learn your voice.</h3>
        <p className="muted small" style={{ margin: '0 0 14px' }}>
          Give us your X handle. Our extension opens your profile in this browser, reads your public tweets, and builds a voice profile. No X API. Takes ~15 seconds.
        </p>

        <div className="handle-input-row">
          <span className="handle-prefix">@</span>
          <input
            className="handle-input"
            value={handle}
            onChange={e => setHandle(e.target.value)}
            placeholder="yourhandle"
            autoFocus
            onKeyDown={e => { if (e.key === 'Enter' && validHandle && !busy) submitHandle(); }}
            disabled={busy}
          />
          <button className="primary" onClick={submitHandle} disabled={busy || !validHandle}>
            {busy ? 'Working…' : 'Connect via extension →'}
          </button>
        </div>

        <ExtensionStatusRow status={extStatus} onRetry={recheckExtension} />

        {busy && progress && (
          <div className="status-row" style={{ marginTop: 12 }}>
            <span className="status-icon running">·</span>
            <span>{progress}</span>
          </div>
        )}
        {err && <div className="error" style={{ marginTop: 12 }}>{err}</div>}

        {!showPaste && (
          <p className="helper" style={{ marginTop: 14 }}>
            Don't want the extension?{' '}
            <button className="link-button" onClick={() => setShowPaste(true)}>Paste a few tweets instead.</button>
            {' · '}
            <button className="link-button" onClick={onSkip}>Skip — use my persona only</button>
          </p>
        )}

        {showPaste && (
          <div className="paste-fallback">
            <label className="field-label" style={{ marginTop: 16 }}>Paste 3+ tweets (one per line, blank line between)</label>
            <textarea
              value={pasted}
              onChange={e => setPasted(e.target.value)}
              placeholder={'Most people overcomplicate AI agents…\n\nDistribution is a daily habit.\n\nShipping fast is useful. Learning fast is the advantage.'}
              style={{ minHeight: 160 }}
              disabled={busy}
            />
            <div className="button-row end" style={{ marginTop: 8 }}>
              <button className="ghost" onClick={() => { setShowPaste(false); setErr(null); }}>Cancel</button>
              <button className="primary" onClick={submitPaste} disabled={busy}>
                {busy ? 'Working…' : 'Use these tweets →'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function ExtensionStatusRow({ status, onRetry }: { status: 'unknown' | 'installed' | 'missing'; onRetry: () => void }) {
  if (status === 'unknown') {
    return <div className="ext-status pending">Checking for the tweetvia extension…</div>;
  }
  if (status === 'installed') {
    return <div className="ext-status ok"><span className="dot" /> Extension connected</div>;
  }
  return (
    <div className="ext-status missing">
      <span className="dot" />
      <span>Extension not detected. <Link to="/app/extension">Install tweetvia</Link>, then <button className="link-button" onClick={onRetry}>retry</button>.</span>
    </div>
  );
}

// =========================================================================
// Stage 2 — Processing (import → voice profile)
// =========================================================================
function ProcessingStage({ api, onDone }: { api: ApiClient; onDone: () => void }) {
  const [step, setStep] = useState(0);
  const [err, setErr] = useState<string | null>(null);
  const [retryKey, setRetryKey] = useState(0);
  const steps = ['Reading your tweets', 'Learning your voice', 'Picking topics to track'];

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setErr(null);
        // Step 0 is already done (tweets imported). We're now on voice.
        setStep(1);
        await api.voiceGenerate();
        if (cancelled) return;
        setStep(2);
        await new Promise(r => setTimeout(r, 400));
        if (cancelled) return;
        onDone();
      } catch (e: any) { if (!cancelled) setErr(e.message); }
    })();
    return () => { cancelled = true; };
    /* eslint-disable-next-line */
  }, [retryKey]);

  return (
    <div className="page">
      <div className="page-head">
        <h1>Setting you up.</h1>
        <p className="lede">Takes about 30 seconds. Stay on this page until it finishes.</p>
      </div>
      <div className="card highlight">
        <ul className="step-list">
          {steps.map((label, i) => (
            <li key={i} className={i < step ? 'done' : i === step ? 'active' : ''}>
              <span className="step-dot">{i < step ? '✓' : i === step ? <span className="spinner-dot" /> : i + 1}</span>
              <span>{label}</span>
            </li>
          ))}
        </ul>
        {err && (
          <>
            <div className="error">{err}</div>
            <div className="button-row" style={{ marginTop: 12 }}>
              <button className="primary" onClick={() => { setStep(0); setRetryKey(k => k + 1); }}>Try again</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// =========================================================================
// Stage 3 — Suggested topics
// =========================================================================
function SuggestStage({ api, me, onDone, onSkip }: { api: ApiClient; me: any; onDone: () => void; onSkip: () => void }) {
  const [topics, setTopics] = useState<SuggestedTopic[] | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const ent = me?.entitlements;
  const freeCap = ent?.maxKeywordMonitors ?? 1;

  useEffect(() => {
    (async () => {
      try {
        const r = await api.suggestTopics();
        setTopics(r.topics ?? []);
      } catch (e: any) { setErr(e.message); }
    })();
    /* eslint-disable-next-line */
  }, []);

  function toggle(topic: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(topic)) next.delete(topic);
      else if (next.size >= freeCap) {
        // Free users can pick only `freeCap` topics here. Replace the first one.
        const first = next.values().next().value;
        if (first) next.delete(first);
        next.add(topic);
      } else {
        next.add(topic);
      }
      return next;
    });
  }

  async function saveAndContinue() {
    if (selected.size === 0) { onSkip(); return; }
    setBusy(true); setErr(null);
    try {
      // Save each selected topic. For each: expand → createMonitor.
      for (const topic of selected) {
        const expanded = await api.expandTopic(topic);
        await api.createMonitor({ name: topic, topic, keywords: expanded.keywords });
      }
      // Kick off a scan in the background so radar has content when they arrive on dashboard.
      api.scanRadar().catch(() => {});
      onDone();
    } catch (e: any) { setErr(e.message); setBusy(false); }
  }

  return (
    <div className="page">
      <div className="page-head">
        <h1>Pick a topic to track.</h1>
        <p className="lede">
          Based on your tweets, here's what you'd have the strongest take on. We'll surface conversations worth engaging with — daily, ranked by leverage.
        </p>
      </div>

      <div className="card highlight">
        {!topics && !err && <div className="status"><div className="spinner-dot" /> Reading your tweets…</div>}
        {err && <div className="error">{err}</div>}

        {topics && (
          <>
            <p className="muted small" style={{ margin: '0 0 12px' }}>
              Pick {freeCap === 1 ? <strong>1 topic</strong> : `up to ${freeCap} topics`} to start.
              {freeCap === 1 && ' Upgrade to Creator to track up to 10.'}
            </p>
            <div className="stack tight">
              {topics.map((t, i) => {
                const isSel = selected.has(t.topic);
                return (
                  <label key={i} className={`topic-pick ${isSel ? 'selected' : ''}`}>
                    <input type="checkbox" checked={isSel} onChange={() => toggle(t.topic)} />
                    <div>
                      <strong>{t.topic}</strong>
                      {t.why && <div className="muted small">{t.why}</div>}
                    </div>
                  </label>
                );
              })}
            </div>
            <div className="wizard-actions">
              <button className="ghost" onClick={onSkip} disabled={busy}>Skip for now</button>
              <button className="primary" onClick={saveAndContinue} disabled={busy}>
                {busy ? 'Setting up…' : selected.size > 0 ? `Track ${selected.size === 1 ? 'this topic' : `${selected.size} topics`} →` : 'Continue →'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// =========================================================================
// Stage 4 — Extension install nudge
// =========================================================================
function ExtensionStage({ onDone }: { onDone: () => void | Promise<void> }) {
  return (
    <div className="page">
      <div className="page-head">
        <h1>One more thing.</h1>
        <p className="lede">
          Install the Chrome extension and you can write tweets in your voice from any page — Hacker News, articles, x.com, anywhere.
        </p>
      </div>
      <div className="card highlight">
        <h3 style={{ margin: '0 0 8px' }}>Chrome extension</h3>
        <ul className="bullet-list">
          <li>Click tweetvia from any page → drafts a tweet about what you're reading.</li>
          <li>Insert directly into the X composer without leaving the page.</li>
          <li>Never auto-posts. You always hit the button.</li>
        </ul>
        <div className="wizard-actions">
          <button className="ghost" onClick={() => onDone()}>I'll do it later</button>
          <Link to="/app/extension"><button className="primary">Install instructions →</button></Link>
        </div>
      </div>
    </div>
  );
}

// =========================================================================
// Dashboard (after onboarding) — Write + Engage in two tabs.
// =========================================================================
type HomeTab = 'write' | 'engage';

function Dashboard({ user, monitors }: { api: ApiClient; user: any; me: any; monitors: any[]; posts: any[]; topPost: any }) {
  const [params, setParams] = useSearchParams();
  // Accept legacy ?tab=draft links and route them to write.
  const rawTab = (params.get('tab') as HomeTab | 'draft') || 'write';
  const tab: HomeTab = rawTab === 'draft' ? 'write' : rawTab;
  const [extDismissed, setExtDismissed] = useState<boolean>(() => localStorage.getItem(EXT_BANNER_DISMISS_KEY) === '1');
  const [showApprovalWelcome, setShowApprovalWelcome] = useState<boolean>(() => {
    const seen = localStorage.getItem(APPROVAL_SEEN_KEY) === '1';
    return !seen;
  });

  const name = (user?.displayName || user?.email?.split('@')[0] || 'there') as string;
  const greeting = greetByTime();

  function switchTab(next: HomeTab) {
    const p = new URLSearchParams(params);
    p.set('tab', next);
    setParams(p);
  }
  function dismissExt() {
    localStorage.setItem(EXT_BANNER_DISMISS_KEY, '1');
    setExtDismissed(true);
  }

  return (
    <div className="page">
      <div className="page-head">
        <h1>{greeting}, {name}.</h1>
        <p className="lede">
          Write tweets in your voice, then find the best conversations to engage with.
        </p>
      </div>

      {showApprovalWelcome && (
        <div className="status-banner" style={{ background: 'rgba(22, 163, 74, 0.08)', borderColor: 'rgba(22, 163, 74, 0.25)' }}>
          <span>🎉 <strong>You're in.</strong> Welcome to the tweetvia beta.</span>
          <button className="status-banner-cta" style={{ background: 'transparent', border: 0, cursor: 'pointer' }} onClick={() => { localStorage.setItem(APPROVAL_SEEN_KEY, '1'); setShowApprovalWelcome(false); }}>
            Dismiss
          </button>
        </div>
      )}

      {!extDismissed && (
        <div className="ext-banner">
          <div>
            <strong>Install the Chrome extension</strong>
            <div className="muted small">Write tweets in your voice from any page. Compiles your Engage feed as you scroll X.</div>
          </div>
          <div className="button-row" style={{ margin: 0 }}>
            <button className="ghost" onClick={dismissExt}>Dismiss</button>
            <Link to="/app/extension"><button className="primary">Install →</button></Link>
          </div>
        </div>
      )}

      <div className="tabs">
        <button className={tab === 'write' ? 'tab active' : 'tab'} onClick={() => switchTab('write')}>Write</button>
        <button className={tab === 'engage' ? 'tab active' : 'tab'} onClick={() => switchTab('engage')}>
          Engage {monitors.length > 0 && <span className="tab-count">{monitors.length}</span>}
        </button>
      </div>

      {tab === 'write' ? <WritePanel /> : <EngagePanel />}
    </div>
  );
}

function greetByTime(): string {
  const h = new Date().getHours();
  if (h < 5) return 'Late night';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  return 'Good evening';
}

// =========================================================================
// Stage 0 — Persona picker (multi-select + freeform)
// =========================================================================
function PersonaStage({ api, user, onDone }: { api: ApiClient; user: any; onDone: () => void }) {
  const [personas, setPersonas] = useState<{ id: string; label: string; short: string }[]>([]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [custom, setCustom] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    api.listPersonas().then(r => setPersonas(r.personas ?? [])).catch(e => setErr(e.message));
    /* eslint-disable-next-line */
  }, []);

  const name = (user?.displayName || user?.email?.split('@')[0] || 'there') as string;
  const canContinue = selected.size > 0 || custom.trim().length > 4;

  function toggle(id: string) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function next() {
    if (!canContinue) return;
    setBusy(true); setErr(null);
    try {
      await api.setPersona({ personas: Array.from(selected), personaCustom: custom.trim() });
      onDone();
    } catch (e: any) { setErr(e.message); setBusy(false); }
  }

  return (
    <div className="page">
      <div className="page-head">
        <h1>Hey {name} — who are you on X?</h1>
        <p className="lede">
          Pick one or more archetypes. Or just describe yourself in the box below. We tune drafts to your voice and the X algorithm.
        </p>
      </div>

      <div className="card highlight onboarding-card">
        <div className="persona-grid">
          {personas.map(p => (
            <label key={p.id} className={`persona-card ${selected.has(p.id) ? 'selected' : ''}`}>
              <input type="checkbox" checked={selected.has(p.id)} onChange={() => toggle(p.id)} />
              <div>
                <strong>{p.label}</strong>
                <div className="muted small">{p.short}</div>
              </div>
            </label>
          ))}
        </div>

        <div className="field" style={{ marginTop: 16 }}>
          <label>Or describe yourself</label>
          <input
            value={custom}
            onChange={e => setCustom(e.target.value)}
            placeholder="What do you do, what do you write about?"
            maxLength={500}
          />
          <p className="helper">Whatever you write here blends with any archetypes you picked above.</p>
        </div>

        {err && <div className="error">{err}</div>}

        <div className="wizard-actions">
          <span />
          <button className="primary" onClick={next} disabled={busy || !canContinue}>
            {busy ? 'Saving…' : 'Continue →'}
          </button>
        </div>
      </div>
    </div>
  );
}
